import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { Nav } from '@accedo/spatial-nav';
import { useCallback, useState } from 'preact/hooks';
import { NavScroll, VERTICAL } from '@accedo/virtual-scroll';
import ReplayButton from '##/assets/icons/icon-Replay.svg';
import { handleVideoClick } from '##/utils/TileClickHandlers';
import { focusCache } from '##/services/store/store';
import ReplayButtonFocused from '##/assets/icons/icon-Replay-Focused.svg';
import GradientBackground from '##/assets/images/VideoUpNextGradientBg.svg';
import { getUnixToDate, expiryToString } from '##/utils/DateTimeHelpers';
import { BaseCarousel, CarouselData } from '../BaseCarousel/BaseCarousel';
import { StandardCarouselItem } from '../StandardCarousel/types';
import { StandardCard } from '../ShowPage/StandardCard';

const TILE_WIDTH_PX = 1078;

const styles = stylesheet`
  @import '##/styles/import.scss';

  .tile-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: pxToVh(162.64);
    font-family: barlow;

    .page-header{
      display:flex;
      flex-direction: row;
      align-items: center;
      margin-left: pxToVw(95);
      margin-top: pxToVw(63);
      .featured-header {
        font-size: pxToVw(22);
        margin-right: pxToVw(880);
      }
    }
    .replay-button {
      position: absolute;
      right: pxToVw(50);
      top: pxToVh(-57);

      &:global(.current-focus) {
        transform: scale(1.1, 1.1);
      }
    }

    .background-image {
        position:absolute;
        z-index: -1;
        height: 100vh;
        width: 100vw;
    }

    .featured-item {
        position: relative;
        display: flex;
        flex-direction: row;
        flex-shrink: 0;
        width: pxToVw(${TILE_WIDTH_PX});
        margin-right: pxToVw(29);
        margin-left: pxToVw(95);
        margin-bottom: pxToVw(41);
        margin-top: pxToVw(23);

        .no-shrink {
          flex-shrink: 0;
        }

    }

    .show-carousel {
        margin-left: pxToVw(95);
        flex-shrink: 0;
        margin-bottom: pxToVw(20);

        .empty-carousel-text {
          margin-top: pxToVh(100);
          font-size: pxToVh(22);
        }
    }

    .poster-carousel {
      margin-left: pxToVw(95);
      flex-shrink: 0;

      .empty-carousel-text {
        margin-top: pxToVh(100);
        font-size: pxToVh(22);
      }
  }
    
    .tile-details {
        opacity: 1;
        padding-top: pxToVh(6);
        margin-left: pxToVw(33);
        flex-shrink: 0;
        max-width: pxToVw(540);

        .title {
            font-size: pxToVh(22);
            margin-bottom: pxToVh(12);
        }

        .description {
            font-size: pxToVh(16);
            line-height: 1.3;
        }
        .dates {
            font-size: pxToVw(16);
            margin: pxToVh(11) 0;
        }
        span {
            color: $search-font-grey;
        }
    }
  }


`;

const truncateString = (text: string, length: number): string => {
  if (!text) return '';
  if (text.length < length) return text;
  return `${text.slice(0, length - 3)}...`;
};

export type VideoUpNextType = {
  featuredData?: StandardCarouselItem;
  railData: CarouselData;
  previousData?: StandardCarouselItem;
  popularShowsData: CarouselData | undefined;
  countDownTimer: number;
  removeCounter: boolean;
};
export const VideoUpNextLayout: FunctionalComponent<VideoUpNextType> = (
  props,
) => {
  const [isFocused, setIsFocused] = useState(false);
  const getExpiryTag = useCallback(() => {
    let expiryStr;
    if (props.featuredData?.expiry) {
      expiryStr = expiryToString(props.featuredData?.expiry);
    }
    return expiryStr;
  }, [props.featuredData?.expiry]);
  const publishedDate = getUnixToDate(
    props.featuredData && props.featuredData.published,
  );
  const expiryDate = getExpiryTag();
  const timerIndicator = `Up Next In ${props.countDownTimer} Seconds `;
  const videoDescription =
    props.featuredData &&
    truncateString(
      props.featuredData.description || props.featuredData.subtitle,
      300,
    );

  const onReplayButtonClick = (): void => {
    if (props.previousData?.playbackApiEndpoint) {
      handleVideoClick(props.previousData, true);
    }
  };

  const onFeaturedVideoOnClick = (): void => {
    if (props.featuredData?.showApiEndpoint) {
      focusCache.tvShowDetails.$set({
        epId: props.featuredData.id,
        seasonTitle: props.featuredData.season,
        isExtra: !!props.featuredData.videoTypeShortForm,
      });
      handleVideoClick(props.featuredData, true);
    }
  };

  const { vodTitle, title, tvShow } = props.featuredData || {};

  return (
    <div className={styles.tileContainer}>
      <GradientBackground className={styles.backgroundImage} />

      <div
        class={styles.pageHeader}
        style={{
          opacity: props.removeCounter ? 0 : 1,
        }}
      >
        <div className={styles.featuredHeader}>{timerIndicator}</div>
      </div>
      <NavScroll
        id={'vertical-layout'}
        orientation={VERTICAL}
        lazyLoadBoundary={3}
        primary
      >
        <Nav.Horizontal id={'featured-area'}>
          <div id={'featured-area'}>
            {props.featuredData && (
              <Nav.Focusable
                id={'featured-video'}
                onSelect={onFeaturedVideoOnClick}
              >
                <div className={styles.featuredItem}>
                  <div className={styles.noShrink}>
                    <StandardCard
                      {...Object.assign(props.featuredData, {
                        tileWidthPx: 475,
                        tileHeightPx: 268,
                      })}
                    />
                  </div>
                  <div className={styles.tileDetails}>
                    <div className={styles.title}>
                      {tvShow}
                      {tvShow && ' - '}
                      {vodTitle || title}
                    </div>
                    <div className={styles.dates}>
                      Air Date: <span>{publishedDate}</span> &nbsp; &nbsp;
                      &nbsp;Expires: <span>{expiryDate}</span>
                    </div>
                    <div className={styles.description}>{videoDescription}</div>
                  </div>
                </div>
              </Nav.Focusable>
            )}

            <Nav.Focusable
              id={'replay-button'}
              onSelect={onReplayButtonClick}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            >
              <div className={styles.replayButton}>
                {isFocused ? <ReplayButtonFocused /> : <ReplayButton />}
              </div>
            </Nav.Focusable>
          </div>
        </Nav.Horizontal>

        <div className={styles.showCarousel}>
          {!props.railData.items?.length ? (
            <div className={styles.emptyCarouselText} />
          ) : (
            <BaseCarousel {...props.railData} />
          )}
        </div>

        {props.popularShowsData && (
          <div className={styles.posterCarousel}>
            <BaseCarousel {...props.popularShowsData} />
          </div>
        )}
      </NavScroll>
    </div>
  );
};
